import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiConfig';
import { recordOAuthLegalConsent } from '../utils/oauthLegalConsent';
import { peekSignupAuthDestination } from '../utils/signupAuthRedirect';

const OAuthCallback = ({ onSuccess, onError }) => {
  const { socialLogin, refreshPendingConsents } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const handledRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  useEffect(() => {
    const handleCallback = async () => {
      if (handledRef.current) {
        return;
      }

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const provider = urlParams.get('provider');
        const oauthError = urlParams.get('error');

        if (oauthError) {
          handledRef.current = true;
          if (oauthError === 'facebook_coming_soon') {
            setError('Facebook login is coming soon! Please use Google or GitHub login for now.');
          } else {
            setError(`Authentication failed: ${oauthError}`);
          }
          setLoading(false);
          setTimeout(() => {
            onErrorRef.current && onErrorRef.current();
          }, 3000);
          return;
        }

        if (!token) {
          if (!handledRef.current) {
            handledRef.current = true;
            setError('No authentication token received');
            setLoading(false);
            setTimeout(() => {
              onErrorRef.current && onErrorRef.current();
            }, 3000);
          }
          return;
        }

        handledRef.current = true;

        // Capture before socialLogin triggers re-renders that could re-run this effect.
        const postAuthDestination = peekSignupAuthDestination() || '/dashboard';

        localStorage.setItem('token', token);

        const apiUrl = getApiUrl();
        const profileUrl = `${apiUrl}/api/auth/profile`;

        const response = await fetch(profileUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch user profile: ${response.status} - ${errorText}`);
        }

        const userData = await response.json();
        await socialLogin(userData, token);

        try {
          await recordOAuthLegalConsent(refreshPendingConsents);
        } catch (consentErr) {
          console.warn('OAuth post-login consent recording failed:', consentErr);
        }

        window.history.replaceState({}, document.title, window.location.pathname);

        if (onSuccessRef.current) {
          await onSuccessRef.current(postAuthDestination);
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError('Authentication failed. Please try again.');
        setLoading(false);
        setTimeout(() => {
          onErrorRef.current && onErrorRef.current();
        }, 3000);
      }
    };

    handleCallback();
  }, [socialLogin, refreshPendingConsents]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white', mb: 3 }} />
        <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
          Completing authentication...
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Please wait while we sign you in
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ mb: 3, maxWidth: 400 }}>
          {error}
        </Alert>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Redirecting to login page...
        </Typography>
      </Box>
    );
  }

  return null;
};

export default OAuthCallback;
